import { alpha, Box, styled, SxProps, Theme, useTheme } from '@mui/material';
import classnames from 'classnames';
import dayjs, { type Dayjs } from 'dayjs';
import { memo, useMemo } from 'react';

import {
  ECaseCardStatus,
  EMaoLegType,
  GlowField,
  ORGAN_PROGRESS_COLORS,
  useCaseBorderHighlight,
  useOverflowMask,
} from '#shared';

import { cardWidthMap, caseStatusMap } from '../../Case';
import { ENOPCaseStatus } from '../../enums';
import { TrialBadge } from '../../TrialProgram';
import { STATUS_COLORS } from '../model';

import { ActiveCaseCardContentCompact } from './ActiveCaseContentCompact';
import { ActiveCaseCardContentFull } from './ActiveCaseContentFull';

import type { ActiveCase } from '../../types';

interface Props {
  activeCase: ActiveCase & {
    updatedFields?: string[];
    isNewOnBoard?: boolean;
  };
  additionalClassName?: string;
  highlightDurationMilliseconds?: number;
  width?: number;
  sx?: SxProps<Theme>;
  onClick?: (id: string) => void;
}

const PREFIX = 'TSMED-ActiveCaseCard';

const activeCaseCardClasses = {
  root: `${PREFIX}-root`,
  header: `${PREFIX}-header`,
  headerLeft: `${PREFIX}-headerLeft`,
  status: `${PREFIX}-status`,
  statusChip: `${PREFIX}-statusChip`,
  unosId: `${PREFIX}-unosId`,
  donorType: `${PREFIX}-donorType`,
  errorIcon: `${PREFIX}-errorIcon`,
  tripType: `${PREFIX}-tripType`,
  tripTime: `${PREFIX}-tripTime`,
  tripCardEta: `${PREFIX}-tripCardEta`,
  tripCardDonorOR: `${PREFIX}-tripCardDonorOR`,
  tripTimer: `${PREFIX}-tripTimer`,
  text: `${PREFIX}-text`,
  footer: `${PREFIX}-footer`,
  trialDonorGroup: `${PREFIX}-trialDonorGroup`,
};

interface RootProps {
  borderColor?: string;
  highlightBorder?: boolean;
  isReturningStatus?: boolean;
  cardWidth: number;
}

const excludedProps: PropertyKey[] = [
  'highlightBorder',
  'borderColor',
  'isReturningStatus',
  'cardWidth',
];

const Root = styled(Box, {
  shouldForwardProp: prop => !excludedProps.includes(prop),
})<RootProps>(({
  theme: { spacing, palette, mixins, utils },
  borderColor,
  highlightBorder,
  isReturningStatus,
  cardWidth,
}) => {
  const isMD = cardWidth <= cardWidthMap.MD;

  return {
    ...mixins.organStyles(palette, { prefix: '&' }),
    borderRadius: '2px',
    display: 'flex',
    flexDirection: 'column',
    padding: spacing(1),
    border: `1px solid ${
      highlightBorder
        ? (borderColor ?? palette.mapBlue[700])
        : palette.mapBlue[700]
    }`,
    backgroundColor: alpha(palette.mapBlue[700], 0.48),
    color: isReturningStatus ? palette.common.white : 'var(--primaryColor)',
    minHeight: 160,
    maxHeight: 200,
    justifyContent: 'space-between',

    ['&:hover']: {
      backgroundColor: alpha(palette.mapBlue[700], 0.6),
      borderColor,
      cursor: 'pointer',
    },

    ['--secondaryColor']: palette.mapBlue[700],
    ['--primaryColor']: palette.success.dark[100],
    [`&.${ECaseCardStatus.Ok}`]: {},
    [`&.${ECaseCardStatus.Accept}`]: {
      backgroundColor: '#00e73319',
    },
    [`&.${ECaseCardStatus.Warning}`]: {
      backgroundColor: 'rgba(255, 56, 68, 0.1)',
    },

    ['&.errorsPageStyles']: {
      margin: 0,
      width: '100%',
      height: '100%',
    },
    [`.${activeCaseCardClasses.header}`]: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing(0, 1),
      marginBottom: spacing(2),
      // wrap on MD and below, single line otherwise
      flexWrap: isMD ? 'wrap' : 'nowrap',
      justifyContent: 'flex-start',
      alignContent: 'flex-start',
      lineHeight: '14px',
      fontSize: utils.clamp(14, 16),
    },

    // Left group (label + donorType)
    [`.${activeCaseCardClasses.headerLeft}`]: {
      display: 'flex',
      flexWrap: 'nowrap',
      whiteSpace: 'nowrap',
      gap: spacing(0, 0.5),
      flex: '0 0 auto',
      minWidth: 0,
      marginBottom: isMD ? '6px' : 0,
    },

    [`.${activeCaseCardClasses.unosId}`]: {
      fontFamily: "'JetBrains Mono', monospace",
      fontFeatureSettings: "'zero' 1",
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      whiteSpace: 'nowrap',
    },

    [`.${activeCaseCardClasses.trialDonorGroup}`]: {
      display: 'flex',
      gap: spacing(1),
      alignItems: 'center',

      [`.${activeCaseCardClasses.donorType}`]: {
        fontFamily: "'JetBrains Mono', monospace",
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
      },
    },

    // Status row container
    [`.${activeCaseCardClasses.status}`]: {
      // >MD: same row, on the right; MD: line 2, align left
      ...(isMD
        ? {
            order: 2,
            flexGrow: 0,
            flexShrink: 0,
            flexBasis: '100%',
            width: '100%',
            marginLeft: 0,
            display: 'flex',
            justifyContent: 'flex-start',
          }
        : {
            order: 1,
            flexGrow: 1,
            flexShrink: 1,
            flexBasis: 'auto',
            marginLeft: 'auto',
            display: 'flex',
            justifyContent: 'flex-end',
          }),
      minWidth: 0,
      gap: spacing(0, 0.5),
      alignItems: 'center',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      direction: 'ltr',
    },

    // Fade only the status chip, never the donorType
    [`.${activeCaseCardClasses.statusChip}`]: {
      display: 'inline-flex',
      alignItems: 'center',
      minWidth: 0,
      whiteSpace: 'nowrap',
      overflow: 'hidden',

      ['> span']: {
        display: 'inline-flex',
        padding: '4px 6px',
        backgroundColor: '#000',
        fontSize: '10px',
        borderRadius: '5px',
      },
    },
    [`.${activeCaseCardClasses.statusChip}[data-fade="1"]`]: {
      WebkitMaskImage:
        'linear-gradient(to right, black 0, black calc(100% - 20px), transparent 100%)',
      maskImage:
        'linear-gradient(to right, black 0, black calc(100% - 20px), transparent 100%)',
    },
    [`.${activeCaseCardClasses.footer}`]: {
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      flexGrow: 1,
      display: 'flex',
      flexWrap: 'wrap',
      letterSpacing: '-1px',
      fontSize: utils.clamp(12, 13.2),
    },

    [`.${activeCaseCardClasses.errorIcon}`]: {
      display: 'flex',
      color: '#ff3844',
    },
    [`.${activeCaseCardClasses.tripTime}`]: {
      fontWeight: '500',
    },
    [`.${activeCaseCardClasses.tripCardEta}`]: {
      color: '#87888b',
      flexGrow: 1,
      textAlign: 'right',
    },
    [`.${activeCaseCardClasses.tripCardDonorOR}`]: {
      color: '#87888b',
      flexGrow: 1,
      textAlign: 'left',
    },
    [`.${activeCaseCardClasses.tripTimer}`]: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexGrow: 1,
    },
  };
});

const LEG_TYPE_BEFORE_DONOR_OR = [
  EMaoLegType['Empty to Donor'],
  EMaoLegType['Team To Donor'],
];
const LEG_TYPE_AFTER_DONOR_OR = [
  EMaoLegType['Team to Hub'],
  EMaoLegType['Empty to Recipient'],
  EMaoLegType['Donor to Recipient'],
];

interface AircraftPoint {
  type: EMaoLegType;
  isActive: boolean | undefined;
  isComplete: boolean | undefined;
  isBeforeDonorOr: boolean;
  registration: string;
  end: Dayjs;
}

const toDonorStatuses = [
  ENOPCaseStatus.ResourcesBooked,
  ENOPCaseStatus.ResourceDeployed,
  ENOPCaseStatus.AtDonorHospital,
];

export const ActiveCaseCard = memo(
  ({
    activeCase,
    additionalClassName = '',
    highlightDurationMilliseconds,
    width = 300,
    sx,
    onClick,
  }: Props) => {
    const {
      label,
      donorType,
      donorORTimeString,
      etaString,
      organ,
      caseTimerEndString,
      showTimer,
      showReturnToBaseState,
      status,
      cardStatus,
      trips = [],
      donorORDateTimeUtc,
      updatedFields,
      id,
    } = activeCase;
    const highlightBorder = useCaseBorderHighlight(updatedFields, false);
    const { palette } = useTheme();
    const sizeSM = width <= cardWidthMap.SM;
    const statusText = caseStatusMap[status];
    const { ref: chipRef, needsFade } = useOverflowMask<HTMLDivElement>([
      statusText,
      width,
    ]);

    // const mainColor = STATUS_COLORS[cardStatus] || organColor;
    const donorOrTime = dayjs(donorORDateTimeUtc);
    const legGroups: AircraftPoint[][] =
      trips?.map(t =>
        t.Legs.map(l => ({
          type: l.LegType,
          isActive:
            (l.Movement?.IsOffBlocks as boolean) && !l.Movement?.IsOnBlocks,
          isComplete: l.Movement?.IsOnBlocks as boolean,
          isBeforeDonorOr: dayjs(l.OnBlocksUtc).isBefore(donorOrTime),
          registration: l.Movement?.Aircraft?.Registration ?? '',
          end: dayjs(l.OnBlocksUtc),
        })),
      ) || [];
    const aircraftsBefore: AircraftPoint[] = [];
    const aircraftsAfter: AircraftPoint[] = [];

    legGroups.forEach(legs => {
      if (!legs.length) {
        return;
      }
      const leg =
        (legs.find(l => l.isActive) || legs.find(l => !l.isComplete)) ??
        (legs.at(-1) as AircraftPoint);
      if (legs.find(l => LEG_TYPE_AFTER_DONOR_OR.includes(l.type))) {
        aircraftsAfter.push(leg);
        return;
      }
      if (legs.find(l => LEG_TYPE_BEFORE_DONOR_OR.includes(l.type))) {
        aircraftsBefore.push(leg);
        return;
      }

      aircraftsAfter.push(leg);
    });

    const donorOrParts = useMemo(
      () => (donorORTimeString ? donorORTimeString.split(' ') : []),
      [donorORTimeString],
    );

    const etaParts = useMemo(
      () => (etaString ? etaString.split(' ') : []),
      [etaString],
    );

    const handleClick = () => onClick?.(id);
    const isCompact = width <= cardWidthMap.MD;
    const isReturningStatus = status === ENOPCaseStatus.ReturningToBase;
    const isToDonorOr = toDonorStatuses.includes(status);
    const showDonorOr = activeCase.showDonorOr && (!isCompact || isToDonorOr);
    const showETA = !showReturnToBaseState && (!isCompact || !isToDonorOr);
    const organBorderColor = ORGAN_PROGRESS_COLORS[organ.name];
    const hasTrial = Boolean(activeCase.trialProgram);

    return (
      <Root
        className={classnames(
          activeCaseCardClasses.root,
          activeCaseCardClasses[cardStatus],
          activeCaseCardClasses[additionalClassName],
          `card-status-${cardStatus}`,
          `organ-type-${organ.name}`,
        )}
        isReturningStatus={isReturningStatus}
        borderColor={organBorderColor}
        highlightBorder={highlightBorder}
        width={width}
        cardWidth={width}
        sx={sx}
        onClick={handleClick}
      >
        <Box className={activeCaseCardClasses.header}>
          {/* LEFT: label (+ donorType unless SM) */}
          <Box className={activeCaseCardClasses.headerLeft}>
            <GlowField
              startColor={organBorderColor}
              className={activeCaseCardClasses.unosId}
              ms={highlightDurationMilliseconds}
              deps={[label]}
              marginRight={0.5}
            >
              {label}
            </GlowField>

            {!sizeSM && (
              <Box className={activeCaseCardClasses.trialDonorGroup}>
                {hasTrial && <TrialBadge />}
                <GlowField
                  startColor={organBorderColor}
                  className={activeCaseCardClasses.donorType}
                  ms={highlightDurationMilliseconds}
                  deps={[donorType]}
                >
                  {donorType}
                </GlowField>
              </Box>
            )}
          </Box>

          {/* RIGHT: status row; donorType appears here on SM */}
          <Box className={activeCaseCardClasses.status}>
            {sizeSM && (
              <Box className={activeCaseCardClasses.trialDonorGroup}>
                {hasTrial && <TrialBadge />}
                <GlowField
                  startColor={organBorderColor}
                  className={activeCaseCardClasses.donorType}
                  ms={highlightDurationMilliseconds}
                  deps={[donorType]}
                >
                  {donorType}
                </GlowField>
              </Box>
            )}

            <Box
              ref={chipRef}
              className={activeCaseCardClasses.statusChip}
              data-fade={needsFade ? 1 : 0}
            >
              <GlowField
                component="span"
                startColor={organBorderColor}
                ms={highlightDurationMilliseconds}
                deps={[statusText]}
              >
                {statusText}
              </GlowField>
            </Box>
          </Box>
        </Box>
        {isCompact ? (
          <ActiveCaseCardContentCompact
            case={activeCase}
            highlightDurationMilliseconds={highlightDurationMilliseconds}
          />
        ) : (
          <ActiveCaseCardContentFull
            cardWidth={width}
            activeCase={activeCase}
            additionalClassName={additionalClassName}
            highlightDurationMilliseconds={highlightDurationMilliseconds}
          />
        )}
        <Box className={activeCaseCardClasses.footer}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              flex: '1 1 auto',
              minWidth: 0,
            }}
          >
            {showDonorOr && (
              <GlowField
                startColor={palette.grey[200]}
                ms={highlightDurationMilliseconds}
                deps={[donorORTimeString]}
                className={classnames(
                  activeCaseCardClasses.tripTime,
                  activeCaseCardClasses.tripCardDonorOR,
                )}
              >
                {`OR ${donorOrParts[0] ?? ''}${donorOrParts[1] ? ` ${donorOrParts[1]}` : ''}`}
              </GlowField>
            )}

            {showTimer && (
              <Box
                component="span"
                className={activeCaseCardClasses.tripTime}
                sx={{
                  letterSpacing: '-1px',
                  color: STATUS_COLORS[cardStatus] || '#EFECEA',
                }}
              >
                {caseTimerEndString}
              </Box>
            )}
          </Box>

          {showETA && (
            <GlowField
              startColor={STATUS_COLORS[cardStatus]}
              ms={highlightDurationMilliseconds}
              deps={[etaString]}
              className={classnames(
                activeCaseCardClasses.tripTime,
                activeCaseCardClasses.tripCardEta,
              )}
            >
              {`TX ${etaParts[0] ?? ''}${etaParts[1] ? ` ${etaParts[1]}` : ''}`}
            </GlowField>
          )}
        </Box>
      </Root>
    );
  },
);

ActiveCaseCard.displayName = 'ActiveCaseCard';
